import React from 'react';
import {
  DEAD_MIN_DAYS,
  EXPIRING_MAX_DAYS,
  EXPIRING_MIN_DAYS,
  getDaysSinceLastMessage,
  getPortLifecycleCategory,
  renderGatewayName,
} from '../utils/portHelpers';
import {
  PortActivationBadge,
  getEffectiveActivationStatus,
  isServiceNumber,
  shouldShowActivate,
  isSimConvertedYes,
  isSimConversionRequested,
  serviceBadgeLabel,
} from '../../../shared/portActivation';

function conversionRequestedTitle(port) {
  const who = (port.req_sim_conversion_by || '').trim();
  const when = port.req_sim_conversion_at
    ? new Date(port.req_sim_conversion_at).toLocaleString()
    : '';
  const requestId = port.req_sim_conversion_id;
  const source = (port.req_sim_conversion_source || '').trim().toLowerCase();
  const parts = [];
  if (requestId) {
    parts.push(source === 'bulk' ? `Bulk request #${requestId}` : `Request #${requestId}`);
  } else if (source === 'bulk') {
    parts.push('Bulk request');
  }
  if (who && when) parts.push(`Requested ${when} by ${who}`);
  else if (when) parts.push(`Requested ${when}`);
  else if (who) parts.push(`Requested by ${who}`);
  return parts.join(' — ') || 'Conversion requested';
}

function rowLifecycleStyle(port) {
  const category = getPortLifecycleCategory(port);
  if (category === 'dead') {
    return { backgroundColor: '#f8d7da' };
  }
  if (category === 'expiring') {
    return { backgroundColor: '#fff3cd' };
  }
  if (category === 'unused') {
    return { backgroundColor: '#cff4fc' };
  }
  return undefined;
}

function DaysSinceCell({ days }) {
  if (days === null) {
    return <span className="badge bg-info text-dark">Never sent</span>;
  }
  if (days === 0) {
    return <span className="text-success fw-bold">Today</span>;
  }
  if (days === 1) {
    return <span className="text-success">1 day</span>;
  }
  if (days < EXPIRING_MIN_DAYS) {
    if (days < 7) return <span className="text-info">{days} days</span>;
    if (days < 30) return <span className="text-warning">{days} days</span>;
    return <span>{days} days</span>;
  }
  if (days <= EXPIRING_MAX_DAYS) {
    return (
      <span className="badge bg-warning text-dark">
        {days} days — expiring
      </span>
    );
  }
  return (
    <span className="badge bg-danger">
      {days} days — dead ({DEAD_MIN_DAYS}+)
    </span>
  );
}

/**
 * Port Table Component
 */
export default function PortTable({
  ports,
  loading,
  gateways,
  portStatus = {},
  handleEdit,
  handleDelete,
  handleArchive,
  handleActivate,
  handleGetNumber,
  gettingNumberPortId = null,
  handleRequestPacCode,
  gettingPacCodePortId = null,
  handleGetBalance,
  gettingBalancePortId = null,
  readOnly = false,
}) {
  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-sm table-striped table-bordered align-middle">
        <thead>
          <tr>
            <th>Gateway</th>
            <th>Source</th>
            <th>Port Number</th>
            <th>Assigned Number</th>
            <th>Mobile Operator</th>
            <th>SIM Converted</th>
            <th>Activation</th>
            <th>User Code</th>
            <th>User Email</th>
            <th>Slack ID</th>
            <th>Credit Balance</th>
            <th>Last Message</th>
            <th>Days Since</th>
            <th>Description</th>
            <th>Created At</th>
            <th>Assigned</th>
            {!readOnly && <th style={{ minWidth: '150px' }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {ports.map((port) => (
            <tr
              key={port.id}
              style={
                port.archived
                  ? { opacity: 0.5, backgroundColor: '#f8d7da' }
                  : rowLifecycleStyle(port)
              }
              title={
                port.archived
                  ? `Archived ${port.archived_at ? new Date(port.archived_at).toLocaleDateString() : ''}`
                  : undefined
              }
            >
              <td>{renderGatewayName(port, gateways)}</td>
              <td>
                {(() => {
                  const label = serviceBadgeLabel(port);
                  if (label === 'Converted') {
                    return <span className="badge bg-secondary">Converted</span>;
                  }
                  if (label === 'Service') {
                    return <span className="badge bg-info text-dark">Service</span>;
                  }
                  return <span className="badge bg-primary">Gateway</span>;
                })()}
              </td>
              <td>
                {port.archived
                  ? String(port.port_number || '').replace(/_archived_\d+$/, '') || '—'
                  : port.port_number || '—'}
              </td>
              <td>
                <div>{port.assigned_number || '—'}</div>
                {(!readOnly &&
                  !port.archived &&
                  !isServiceNumber(port) &&
                  (handleGetNumber || handleRequestPacCode)) && (
                  <div className="d-flex flex-column align-items-start gap-1 mt-1">
                    {handleGetNumber && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm py-0 px-2"
                        style={{ fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}
                        onClick={() => handleGetNumber(port)}
                        disabled={
                          gettingNumberPortId === port.id ||
                          gettingBalancePortId === port.id ||
                          gettingPacCodePortId === port.id
                        }
                        title="Query SIM number (port must be active)"
                      >
                        {gettingNumberPortId === port.id ? 'Getting…' : 'Check Number'}
                      </button>
                    )}
                    {handleRequestPacCode && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm py-0 px-2"
                        style={{ fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}
                        onClick={() => handleRequestPacCode(port)}
                        disabled={
                          gettingNumberPortId === port.id ||
                          gettingBalancePortId === port.id ||
                          gettingPacCodePortId === port.id
                        }
                        title="Send PAC request SMS (port must be active)"
                      >
                        {gettingPacCodePortId === port.id ? 'Sending…' : 'Get Pack Code'}
                      </button>
                    )}
                  </div>
                )}
              </td>
              <td>{port.mobile_operator || '—'}</td>
              <td
                title={
                  [
                    port.sim_converted_on
                      ? `Converted on ${new Date(port.sim_converted_on).toLocaleString()}`
                      : null,
                    port.sim_converted_note || null,
                    isSimConversionRequested(port) && !isSimConvertedYes(port)
                      ? conversionRequestedTitle(port)
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' — ') || undefined
                }
              >
                {isSimConvertedYes(port) ? (
                  'Yes'
                ) : isSimConversionRequested(port) ? (
                  <span
                    className="badge"
                    style={{
                      backgroundColor: '#fd7e14',
                      color: '#fff',
                      fontWeight: 600,
                    }}
                  >
                    Requested
                    {port.req_sim_conversion_id ? ` #${port.req_sim_conversion_id}` : ''}
                    {port.req_sim_conversion_source === 'bulk' ? ' (bulk)' : ''}
                  </span>
                ) : port.is_sim_converted === 1 ? (
                  'No'
                ) : (
                  '—'
                )}
              </td>
              <td>
                <PortActivationBadge
                  statusEntry={portStatus[port.id]}
                  compact
                  showTrafficLight={false}
                />
              </td>
              <td>{port.user_code}</td>
              <td>{port.user_email}</td>
              <td>{port.slack_user_id}</td>
              <td>
                <div>
                  {port.credit_balance !== null &&
                  port.credit_balance !== undefined &&
                  port.credit_balance !== '' ? (
                    `£${Number(port.credit_balance).toFixed(2)}`
                  ) : (
                    '-'
                  )}
                </div>
                {!readOnly && !port.archived && handleGetBalance && !isServiceNumber(port) && (
                  <div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      onClick={() => handleGetBalance(port)}
                      disabled={
                        gettingBalancePortId === port.id ||
                        gettingNumberPortId === port.id ||
                        gettingPacCodePortId === port.id
                      }
                      title="Query SIM balance (port must be active)"
                    >
                      {gettingBalancePortId === port.id ? 'getting…' : 'Get balance'}
                    </button>
                  </div>
                )}
              </td>
              <td>
                {port.last_message_sent
                  ? new Date(
                      port.last_message_sent
                    ).toLocaleDateString()
                  : '-'}
              </td>
              <td>
                <DaysSinceCell days={getDaysSinceLastMessage(port.last_message_sent)} />
              </td>
              <td>{port.description}</td>
              <td>
                {new Date(port.created_at).toLocaleString()}
              </td>
              <td>
                <span
                  className={`badge ${
                    port.assigned
                      ? 'bg-success'
                      : 'bg-secondary'
                  }`}
                >
                  {port.assigned ? 'Yes' : 'No'}
                </span>
              </td>
              {!readOnly && (
                <td>
                  {(() => {
                    const st = getEffectiveActivationStatus(portStatus[port.id] || {});
                    const isActive = st === 'active' || st === 'activating';
                    const isQueued = st === 'queued';
                    const converted = !shouldShowActivate(port);
                    if (converted) {
                      return (
                        <span
                          className="badge bg-secondary me-1"
                          title={
                            isSimConvertedYes(port)
                              ? 'SIM already converted — activation not available'
                              : 'Service number — activation not available'
                          }
                        >
                          {isSimConvertedYes(port) ? 'Converted' : 'Service'}
                        </span>
                      );
                    }
                    const disabled = isActive || isQueued || port.archived;
                    const btnClass = isActive
                      ? 'btn btn-warning btn-sm me-1'
                      : isQueued
                      ? 'btn btn-outline-secondary btn-sm me-1'
                      : 'btn btn-success btn-sm me-1';
                    const label = isActive ? 'Force Activate' : isQueued ? 'In Queue' : 'Activate';
                    const title = isQueued
                      ? 'Already queued — waiting for its turn'
                      : isActive
                      ? 'Force a fresh activation'
                      : 'Activate this SIM';
                    return (
                      <button
                        className={btnClass}
                        onClick={() => handleActivate(port)}
                        disabled={disabled}
                        title={title}
                      >
                        {label}
                      </button>
                    );
                  })()}
                  <button
                    className="btn btn-primary btn-sm me-1"
                    onClick={() => handleEdit(port)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-warning btn-sm me-1"
                    onClick={() => handleArchive(port.id)}
                    title="Archive this number — marks as replaced, preserves history"
                  >
                    Replace
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(port.id)}
                  >
                    Delete
                  </button>
                </td>
              )}
            </tr>
          ))}

          {ports.length === 0 && !loading && (
            <tr>
              <td
                colSpan={16}
                className="text-center text-muted py-3"
              >
                No matching ports
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


